/**
 * EVM token configuration
 */

import { EvmNetworks, Networks } from "../../helpers";
import { ERC20_EURC_BASE, ERC20_EURC_BASE_DECIMALS } from "../constants/misc";
import { PENDULUM_USDC_AXL } from "../pendulum/config";
import { TokenType } from "../types/base";
import { EvmToken, EvmTokenDetails } from "../types/evm";

export const evmTokenConfig: Record<EvmNetworks, Partial<Record<EvmToken, EvmTokenDetails>>> = {
  [Networks.Ethereum]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      isNative: false,
      network: Networks.Ethereum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 6,
      erc20AddressSourceChain: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
      isNative: false,
      network: Networks.Ethereum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.ETH]: {
      assetSymbol: "ETH",
      decimals: 18,
      erc20AddressSourceChain: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      isNative: true,
      network: Networks.Ethereum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0x1B81D678ffb9C0263b24A97847620C99d213eB14",
      isNative: false,
      network: Networks.Ethereum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.Polygon]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359",
      isNative: false,
      network: Networks.Polygon,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDCE]: {
      assetSymbol: "USDC.e",
      decimals: 6,
      erc20AddressSourceChain: "0x2791bca1f2de4661ed88a30c99a7a9449aa84174",
      isNative: false,
      network: Networks.Polygon,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 6,
      erc20AddressSourceChain: "0xc2132d05d31c914a87c6611c10748aeb04b58e8f",
      isNative: false,
      network: Networks.Polygon,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0x750e4c4984a9e0f12978ea6742bc1c5d248f40ed",
      isNative: false,
      network: Networks.Polygon,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.PolygonAmoy]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0x41e94eb019c0762f9bfcf9fb1e58725bfb0e7582",
      isNative: false,
      network: Networks.PolygonAmoy,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.BSC]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 18,
      erc20AddressSourceChain: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
      isNative: false,
      network: Networks.BSC,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 18,
      erc20AddressSourceChain: "0x55d398326f99059fF775485246999027B3197955",
      isNative: false,
      network: Networks.BSC,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.ETH]: {
      assetSymbol: "ETH",
      decimals: 18,
      erc20AddressSourceChain: "0x2170ed0880ac9a755fd29b2688956bd959f933f8",
      isNative: false,
      network: Networks.BSC,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0x4268B8F0B87b6Eae5d897996E6b845ddbD99Adf3",
      isNative: false,
      network: Networks.BSC,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.Arbitrum]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
      isNative: false,
      network: Networks.Arbitrum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDCE]: {
      assetSymbol: "USDC.e",
      decimals: 6,
      erc20AddressSourceChain: "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8",
      isNative: false,
      network: Networks.Arbitrum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 6,
      erc20AddressSourceChain: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
      isNative: false,
      network: Networks.Arbitrum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.ETH]: {
      assetSymbol: "ETH",
      decimals: 18,
      erc20AddressSourceChain: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      isNative: true,
      network: Networks.Arbitrum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0xEB466342C4d449BC9f53A865D5Cb90586f405215",
      isNative: false,
      network: Networks.Arbitrum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.MORPHO_VAULT]: {
      assetSymbol: "VAULT",
      decimals: 18,
      erc20AddressSourceChain: "0x0bF0164D17469241B6E086dA4016DCc54FEAA334", // Alphaping USDC vault on Ethereum
      isNative: false,
      logoURI: "https://cdn.morpho.org/v2/assets/images/favicon.svg",
      network: Networks.Ethereum,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.Base]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
      isNative: false,
      network: Networks.Base,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 6,
      erc20AddressSourceChain: "0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2",
      isNative: false,
      network: Networks.Base,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.ETH]: {
      assetSymbol: "ETH",
      decimals: 18,
      erc20AddressSourceChain: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      isNative: true,
      network: Networks.Base,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0xEB466342C4d449BC9f53A865D5Cb90586f405215",
      isNative: false,
      network: Networks.Base,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.BRLA]: {
      assetSymbol: "BRLA",
      decimals: 18,
      erc20AddressSourceChain: "0xfCB34c47f850f452C15EA1B84d51231C38A61783",
      isNative: false,
      network: Networks.Base,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.EURC]: {
      assetSymbol: "EURC",
      decimals: ERC20_EURC_BASE_DECIMALS,
      erc20AddressSourceChain: ERC20_EURC_BASE,
      isNative: false,
      network: Networks.Base,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.BaseSepolia]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0x1b888723fb7699f9dF0a99443107E8A888A67e11", // Mock USDC contract
      isNative: false,
      network: Networks.BaseSepolia,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 6,
      erc20AddressSourceChain: "0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2",
      isNative: false,
      network: Networks.BaseSepolia,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.ETH]: {
      assetSymbol: "ETH",
      decimals: 18,
      erc20AddressSourceChain: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      isNative: true,
      network: Networks.BaseSepolia,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0xEB466342C4d449BC9f53A865D5Cb90586f405215",
      isNative: false,
      network: Networks.BaseSepolia,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.BRLA]: {
      assetSymbol: "BRLA",
      decimals: 18,
      erc20AddressSourceChain: "0x57180796D4082Ba903d86c4eA3C86490fA10512c", // Mock BRLA contract
      isNative: false,
      network: Networks.BaseSepolia,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.Avalanche]: {
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
      isNative: false,
      network: Networks.Avalanche,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDT]: {
      assetSymbol: "USDT",
      decimals: 6,
      erc20AddressSourceChain: "0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7",
      isNative: false,
      network: Networks.Avalanche,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDCE]: {
      assetSymbol: "USDC.e",
      decimals: 6,
      erc20AddressSourceChain: "0xA7D7079b0FEaD91F3e65f86E8915Cb59c1a4C664",
      isNative: false,
      network: Networks.Avalanche,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0xfaB550568C688d5D8A52C7d794cb93Edc26eC0eC",
      isNative: false,
      network: Networks.Avalanche,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  },
  [Networks.Moonbeam]: {
    [EvmToken.AXLUSDC]: {
      assetSymbol: "USDC.axl",
      decimals: 6,
      erc20AddressSourceChain: "0xca01a1d0993565291051daff390892518acfad3a",
      isNative: false,
      network: Networks.Moonbeam,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    },
    [EvmToken.USDC]: {
      assetSymbol: "USDC",
      decimals: 6,
      erc20AddressSourceChain: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      isNative: false,
      network: Networks.Moonbeam,
      pendulumRepresentative: PENDULUM_USDC_AXL,
      type: TokenType.Evm
    }
  }
};
